import { v4 as uuidv4 } from 'uuid';
import { CluTickItem } from '../@models/clutick.model';
import { ZTicketVaultDb, ZTicketVaultDocument, ZTicketVaultStatus } from './zticketvault-document.model';
import { Injectable } from '@angular/core';

export class ZTicketVaultDocumentsPerimetre {
  CAISSE_SESSION_ITEMS : string[]; 
  FLT_PERIOD_ENABLED : boolean;
  BORNE_INF :  Date | undefined;
  BORNE_SUP :  Date | undefined;
}

// ====== Service ======
@Injectable()
export class ZTicketVaultDbDocumentDaoService {
  private readonly db: ZTicketVaultDb;

  constructor(db?: ZTicketVaultDb) {
    this.db = db ?? new ZTicketVaultDb();
  }

  /** Upsert d’un ticket complet dans la Vault (PK = MD_DOGID). */
  async vaultUpsertTicket(item: CluTickItem, status: ZTicketVaultStatus): Promise<ZTicketVaultDocument> {
    if (!item?.ENTETE) {
      throw new Error('vaultUpsertTicket: ENTETE is required');
    }

    // 1) ENTETE -> plain object (RxDocument ou déjà plain)
    const entetePlain = this.toPlain(item.ENTETE);

    // 2) Garantir un MD_DOGID stable dans l'entête
    const MD_DOGID = this.ensureMdDogid(entetePlain);

    // 3) LIGNES/REGLEMENTS -> plain objects
    const lignesPlain = (item.LIGNES ?? []).map(x => this.toPlain(x));
    const reglementsPlain = (item.REGLEMENTS ?? []).map(x => this.toPlain(x));

    // 4) Snapshot complet
    const snapshot = this.deepCloneSafe({
      ENTETE: entetePlain,
      LIGNES: lignesPlain,
      REGLEMENTS: reglementsPlain
    });

    // 5) Champs miroir + doc Vault
    const existing = await this.db.tickets.get(MD_DOGID);
    const ts = Date.now();

    const doc: ZTicketVaultDocument = {
      MD_DOGID: MD_DOGID,
      createdAt: existing?.createdAt ?? ts,
      updatedAt: ts,
      status,

      MD_SITEGID: entetePlain.MD_SITEGID,
      MD_SITE: entetePlain.MD_SITE,
      MD_CAISSE: entetePlain.MD_CAISSE,
      MD_CAISSIER: entetePlain.MD_CAISSIER,
      MD_CAISSE_SESSION: entetePlain.MD_CAISSE_SESSION,
      MD_DATE_CREATION: entetePlain.MD_DATE_CREATION,
      DO_DATE: entetePlain.DO_DATE,

      DO_PIECE: entetePlain.DO_PIECE,
      DO_PIECE_INTERNAL: entetePlain.DO_PIECE_INTERNAL,
      DO_PIECE_INTERNAL_PREVIOUS: entetePlain.DO_PIECE_INTERNAL_PREVIOUS ?? null,

      DO_TOTALHT: entetePlain.DO_TOTALHT,
      DO_TOTALTTC: entetePlain.DO_TOTALTTC,
      DO_NETAPAYER: entetePlain.DO_NETAPAYER,
      DO_SOLDETTC: entetePlain.DO_SOLDETTC,
      DO_MONTANTREGLE: entetePlain.DO_MONTANTREGLE,

      ZTICKET_STATUS: entetePlain.ZTICKET_STATUS,
      ZSYNCUP_STATUS: entetePlain.ZSYNCUP_STATUS,
      ZSYNCUP_FLAG: entetePlain.ZSYNCUP_FLAG,
      ZSYNCUP_ERROR_MESSAGE: entetePlain.ZSYNCUP_ERROR_MESSAGE,

      ZV_BUSKEY: this.buildBusKey(entetePlain),

      ticket: snapshot
    };

    // 6) Upsert en IndexedDB (idempotent)
    await this.db.tickets.put(doc);

    return doc;
  }

  async getTicket(MD_DOGID: string): Promise<ZTicketVaultDocument | undefined> {
    return this.db.tickets.get(MD_DOGID);
  }

  async listRecent(limit = 50): Promise<ZTicketVaultDocument[]> {
    return this.db.tickets.orderBy('updatedAt').reverse().limit(limit).toArray();
  }

  async doGetCaisseSessionItems_ZTicketVault(): Promise<string[]> {
    const results = await this.db.tickets.orderBy('MD_CAISSE_SESSION').uniqueKeys();
    return results as string[];
  }

  async doGetTicketItems_ZTicketVault(perimetre : ZTicketVaultDocumentsPerimetre): Promise<ZTicketVaultDocument[]> {
    let query = this.db.tickets.orderBy('updatedAt').reverse();
    if ( perimetre.FLT_PERIOD_ENABLED && perimetre.BORNE_INF && perimetre.BORNE_SUP ) {
      query = query.filter(t =>
        t.createdAt >= perimetre.BORNE_INF!.getTime() &&
        t.createdAt <= perimetre.BORNE_SUP!.getTime()
      );
    }

    let items = await query.toArray();

    if (perimetre.CAISSE_SESSION_ITEMS?.length) {
      items = items.filter(t => perimetre.CAISSE_SESSION_ITEMS.includes(t.MD_CAISSE_SESSION!) );
    }

    return items;
  }

  async deleteTicket(MD_DOGID: string): Promise<void> {
    await this.db.tickets.delete(MD_DOGID);
  }

  // ====== Helpers ======

  /** Convertit un objet RxDocument (RxDB) ou plain object en plain JSON. */
  private toPlain<T = any>(obj: any): T {
    if (!obj) return obj;

    // RxDB RxDocument a souvent toJSON()
    if (typeof obj.toJSON === 'function') {
      return obj.toJSON();
    }

    // Certains modèles peuvent exposer _data (à éviter si possible)
    // On reste conservateur : si c'est déjà un objet simple, on le renvoie.
    return obj as T;
  }

  /** Garantit la présence d’un MD_DOGID stable */
  private ensureMdDogid(entetePlain: any): string {
    if (!entetePlain.MD_DOGID || typeof entetePlain.MD_DOGID !== 'string' || entetePlain.MD_DOGID.length < 8) {
      entetePlain.MD_DOGID = uuidv4();
    }
    return entetePlain.MD_DOGID;
  }

  private buildBusKey(entetePlain: any): string {
    const site = entetePlain.MD_SITEGID ?? '';
    const caisse = entetePlain.MD_CAISSE ?? '';
    const session = entetePlain.MD_CAISSE_SESSION ?? '';
    const piece = entetePlain.DO_PIECE_INTERNAL ?? '';
    return `${site}|${caisse}|${session}|${piece}`;
  }

  /**
   * Clone profond “safe” :
   * - utilise structuredClone si dispo
   * - sinon fallback JSON stringify/parse
   */
  private deepCloneSafe<T>(obj: T): T {
    const sc: any = (globalThis as any).structuredClone;
    if (typeof sc === 'function') {
      return sc(obj);
    }
    // fallback (attention: perd Date/Map/Set/function, OK pour JSON)
    return JSON.parse(JSON.stringify(obj));
  }
}
